<template>
  <main class="manage-bank">
    <Loader v-if="loading" />
    
    <!-- Header -->
    <div class="manage-bank__header">
      <div class="manage-bank__header-content">
        <h1 class="manage-bank__title">Manage Bank</h1>
        <p class="manage-bank__subtitle">Link and manage your bank accounts</p>
      </div>
      <button
        class="manage-bank__add-btn"
        @click="showAddBankForm = true"
        v-can.disable="'bank:updateBank'"
        v-if="bankAccounts && bankAccounts.length > 0"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3.75V14.25M3.75 9H14.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Add Bank
      </button>
    </div>

    <!-- Bank Accounts List -->
    <div class="manage-bank__content" v-if="bankAccounts && bankAccounts.length > 0">
      <div class="manage-bank__list">
        <div
          class="manage-bank__card"
          v-for="bank in bankAccounts"
          :key="bank._id"
        >
          <div class="manage-bank__card-icon">
            <img v-if="bank.image" :src="bank.image" :alt="bank.name" />
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9.5L12 4L21 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 11V19H19V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 21H22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 15H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="manage-bank__card-info">
            <span class="manage-bank__card-name">{{ bank.accountName }}</span>
            <span class="manage-bank__card-number">{{ bank.number }}</span>
            <span class="manage-bank__card-bank">{{ bank.name }}</span>
          </div>
          <button
            class="manage-bank__card-delete"
            @click="removeBank(bank._id)"
            v-can.disable="'bank:updateBank'"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.25 4.5H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5H14.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.5 8.25V12.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.5 8.25V12.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="manage-bank__empty" v-else>
      <div class="manage-bank__empty-icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 19L24 8L42 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 22V38H38V22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M4 42H44" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 30H30" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="manage-bank__empty-title">No bank accounts linked</h2>
      <p class="manage-bank__empty-text">Add a bank account to enable withdrawals</p>
      <button
        class="manage-bank__empty-btn"
        @click="showAddBankForm = true"
        v-can.disable="'bank:updateBank'"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3.75V14.25M3.75 9H14.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Add Bank Account
      </button>
    </div>

    <!-- Add Bank Modal -->
    <Teleport to="#teleport-modal">
      <AddBankModal
        v-if="showAddBankForm"
        title="Add Bank Account"
        subTitle="Link a bank account for withdrawals"
        @close="showAddBankForm = false"
        @goBack="showAddBankForm = false"
        :bgChange="true"
      >
        <template v-slot:content>
          <div class="tw-px-4 lg:tw-px-6">
            <SelectCountryForm @bank-connected="showAddBankForm = false" />
          </div>
        </template>
      </AddBankModal>
    </Teleport>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SelectCountryForm from "@/components/general/SelectCountryForm";
import AddBankModal from "@/components/general/sidemodal/SideModal";
export default {
  name: "ManageBank",
  data() {
    return {
      showAddBankForm: false,
      loading: false,
    };
  },
  components: { SelectCountryForm, AddBankModal },
  computed: {
    ...mapGetters("Account", ["bankAccounts"]),
  },
  watch: {
    showAddBankForm(val) {
      if (!val) {
        this.fetchBankAccounts();
      }
    },
  },
  created() {
    this.fetchBankAccounts();
  },
  methods: {
    ...mapActions("Account", ["fetchBankAccounts", "deleteBank"]),
    ...mapActions(["showToast"]),
    async removeBank(id) {
      this.loading = true;
      try {
        const response = await this.deleteBank(id);
        this.loading = false;
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.fetchBankAccounts();
      } catch (error) {
        this.loading = false;
        return error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.manage-bank {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__header-content {
    flex: 1;
    min-width: 200px;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #3B6FE8;
    }
  }

  &__content {
    padding: 24px;
  }

  &__list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__card {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    transition: all 0.15s ease;

    &:hover {
      border-color: #4D84FF;
      background: white;
    }
  }

  &__card-icon {
    width: 48px;
    height: 48px;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    color: #64748B;

    img {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }
  }

  &__card-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__card-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__card-number {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
  }

  &__card-bank {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #64748B;
  }

  &__card-delete {
    width: 36px;
    height: 36px;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #94A3B8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
    flex-shrink: 0;

    &:hover {
      background: #FEF2F2;
      color: #EF4444;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    text-align: center;
  }

  &__empty-icon {
    width: 80px;
    height: 80px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94A3B8;
    margin-bottom: 20px;
  }

  &__empty-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__empty-text {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0 0 24px 0;
  }

  &__empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #3B6FE8;
    }
  }
}
</style>
